// The Rules view (prototype `rulesView`): one block per attribute set, one
// row per "or" condition, one value picker per deciding column. A standard
// set's conditions can be changed only where the application allows it.

import { ChevronDown, ChevronRight, Plus, Trash2, X } from 'lucide-react'
import { tw } from '../../testHooks'
import type { UdaRule, UdaSet } from '../types'
import { ANY, canonValue } from '../rules'
import { canEdit, driverLabel, editableLeaves, formOf, SET_TENANT_MAY_KEYS, valueSummary } from './model'
import { opForm, opSet, useSetup } from './state'
import { setCover } from './coverage'
import { useValuesByDriver } from './StepCombinations'
import { SetDot } from './ui'

export default function RulesView() {
  const { formNo, draft, mode, labelOf, domains, openDialog, go, notify } = useSetup()
  const form = formOf(draft.layer, formNo)
  const { values } = useValuesByDriver(formNo)
  const drivers = form?.drivers || []
  const lab = labelOf(formNo)
  if (!form) return null
  const total = drivers.reduce((n, d) => n * (values[d.key]?.length || 0), drivers.length ? 1 : 0)
  const isPublishedSet = (s: UdaSet) => (s.columns || []).some(c => draft.published.has(`${formNo}|${c.name}`))

  const addRule = (s: UdaSet) => {
    const r: UdaRule = {}
    for (const d of drivers) r[d.key] = ANY
    if (drivers[0]) r[drivers[0].key] = []
    const idx = (s.rules || []).length
    draft.edit(opSet(formNo, s.key, set => { set.rules = [...(set.rules || []), r] }))
    if (drivers[0]) openDialog({ kind: 'picker', set: s.key, rule: idx, driver: drivers[0].key })
  }
  const delRule = (s: UdaSet, i: number) => draft.edit(opSet(formNo, s.key, set => {
    set.rules = (set.rules || []).filter((_, k) => k !== i)
  }))
  const delSet = (s: UdaSet) => {
    if (mode === 'tenant' && s.origin === 'design') { notify('info', 'This attribute set comes with the application and cannot be deleted.'); return }
    if (mode === 'tenant' && isPublishedSet(s)) { notify('info', 'This set has published columns. Retire its columns first.'); return }
    const k = s.key
    draft.edit(opForm(formNo, f => { f.sets = f.sets.filter(x => x.key !== k) }))
  }

  return (
    <div className="uds-tblwrap">
      <table className="uds-tbl rules">
        <thead>
          <tr>
            <th>Attribute set</th><th />
            {drivers.map(d => (
              <th key={d.key}>{driverLabel(d)}
                <div className="uds-thsub">{domains.domainOf(formNo, d).total} values · {d.from.kind === 'master' ? d.from.column : `form.${d.from.column}`}</div>
              </th>
            ))}
            <th /><th className="r">Covers</th><th className="r">Columns</th><th />
          </tr>
        </thead>
        {(form.sets || []).map(s => {
          const leaves = editableLeaves(mode, s, SET_TENANT_MAY_KEYS)
          const canRules = canEdit(leaves, 'rules')
          const canLabel = canEdit(leaves, 'label')
          const rules = s.rules || []
          const rows = rules.length ? rules : [null]
          const span = Math.max(1, rules.length)
          const cov = setCover(form, s.key, values)
          return (
            <tbody key={s.key} className="setgrp">
              {rows.map((r, ri) => (
                <tr key={ri} className={ri ? 'or' : ''}>
                  {ri === 0 && (
                    <td rowSpan={span} className="setcell">
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <SetDot color={s.color} />
                        <input className="uds-inp sm" value={s.label || ''} placeholder={s.key} aria-label="Attribute set name"
                          disabled={!canLabel}
                          onChange={e => { const v = e.target.value; draft.edit(opSet(formNo, s.key, set => { set.label = v })) }} />
                      </span>
                    </td>
                  )}
                  <td className="uds-ortag">{ri ? 'or' : 'when'}</td>
                  {r ? drivers.map(d => {
                    const v = r[d.key]
                    const any = canonValue(v) === ANY
                    const n = Array.isArray(canonValue(v)) ? (canonValue(v) as string[]).length : 0
                    return (
                      <td key={d.key}>
                        <button type="button" className={`uds-pick ${any ? 'any' : ''}`} disabled={!canRules}
                          aria-label={`${driverLabel(d)}: ${valueSummary(v, d.key, lab)}`}
                          onClick={() => openDialog({ kind: 'picker', set: s.key, rule: ri, driver: d.key })}
                          {...tw('action', `uda-pick:${s.key}:${ri}:${d.key}`)}>
                          <span className="pl">{valueSummary(v, d.key, lab)}</span>
                          {!any && n > 0 && <span className="pc">{n}</span>}
                          <ChevronDown size={14} />
                        </button>
                      </td>
                    )
                  }) : <td colSpan={drivers.length}><span className="uds-na">Applies to nobody yet</span></td>}
                  <td>
                    {r && rules.length > 1 && canRules && (
                      <button type="button" className="uds-icbtn" aria-label="Remove this condition" onClick={() => delRule(s, ri)}><X size={15} /></button>
                    )}
                  </td>
                  {ri === 0 && (
                    <>
                      <td rowSpan={span} className="r"><b>{cov.covered.toLocaleString()}</b>
                        <span className="uds-na" style={{ fontStyle: 'normal' }}> of {(total || cov.all).toLocaleString()}</span></td>
                      <td rowSpan={span} className="r">
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => go({ t: 'set', set: s.key })}>
                          {(s.columns || []).length} columns <ChevronRight size={14} />
                        </button>
                      </td>
                      <td rowSpan={span} style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" className="uds-icbtn" title="Add an 'or' condition" aria-label="Add an or condition"
                          disabled={!canRules} onClick={() => addRule(s)}><Plus size={15} /></button>
                        <button type="button" className="uds-icbtn" aria-label="Delete attribute set" onClick={() => delSet(s)}
                          disabled={mode === 'tenant' && s.origin === 'design'}><Trash2 size={15} /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          )
        })}
        {!(form.sets || []).length && (
          <tbody><tr><td colSpan={drivers.length + 6} className="uds-na" style={{ padding: 16 }}>No attribute sets yet. Add the first one.</td></tr></tbody>
        )}
      </table>
    </div>
  )
}
